import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { KeyRound, Eye, EyeOff, Shield, CheckCircle2 } from "lucide-react";
import { apiRequest, removeToken, removeUser } from "../lib/utils";
import { toast } from "../components/ToastProvider";

export const TrocarSenha = () => {
  const navigate = useNavigate();
  const [senhaAtual, setSenhaAtual] = useState("");
  const [novaSenha, setNovaSenha] = useState("");
  const [confirmarSenha, setConfirmarSenha] = useState("");
  const [showSenhaAtual, setShowSenhaAtual] = useState(false);
  const [showNovaSenha, setShowNovaSenha] = useState(false);
  const [showConfirmar, setShowConfirmar] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!senhaAtual || !novaSenha || !confirmarSenha) {
      setError("Preencha todos os campos");
      return;
    }

    if (novaSenha.length < 6) {
      setError("A nova senha deve ter pelo menos 6 caracteres");
      return;
    }

    if (novaSenha !== confirmarSenha) {
      setError("As senhas não coincidem");
      return;
    }

    if (senhaAtual === novaSenha) {
      setError("A nova senha deve ser diferente da atual");
      return;
    }

    setLoading(true);

    try {
      await apiRequest("/auth/trocar-senha-primeiro-login", {
        method: "POST",
        body: JSON.stringify({
          senha_atual: senhaAtual,
          nova_senha: novaSenha,
        }),
      });

      toast.success("Senha alterada com sucesso!");
      
      // Atualizar o localStorage para remover o flag
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      delete user.deve_trocar_senha;
      localStorage.setItem("user", JSON.stringify(user));
      
      navigate("/dashboard");
    } catch (err) {
      setError(err.message || "Erro ao alterar senha");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    removeToken();
    removeUser();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="text-center space-y-4">
          <div className="mx-auto w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center">
            <Shield className="w-8 h-8 text-teal-600" />
          </div>
          <CardTitle className="text-2xl" style={{ fontFamily: 'Manrope' }}>
            Criar Nova Senha
          </CardTitle>
          <CardDescription>
            Por segurança, você precisa criar uma nova senha antes de continuar.
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Senha Atual/Temporária */}
            <div className="space-y-2">
              <Label htmlFor="senha-atual">Senha Temporária (recebida por email)</Label>
              <div className="relative">
                <Input
                  id="senha-atual"
                  type={showSenhaAtual ? "text" : "password"}
                  value={senhaAtual}
                  onChange={(e) => setSenhaAtual(e.target.value)}
                  placeholder="Digite a senha temporária"
                  className="pr-10"
                  data-testid="senha-atual-input"
                />
                <button
                  type="button"
                  onClick={() => setShowSenhaAtual(!showSenhaAtual)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showSenhaAtual ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Nova Senha */}
            <div className="space-y-2">
              <Label htmlFor="nova-senha">Nova Senha</Label>
              <div className="relative">
                <Input
                  id="nova-senha"
                  type={showNovaSenha ? "text" : "password"}
                  value={novaSenha}
                  onChange={(e) => setNovaSenha(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  className="pr-10"
                  data-testid="nova-senha-input"
                />
                <button
                  type="button"
                  onClick={() => setShowNovaSenha(!showNovaSenha)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showNovaSenha ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Confirmar Senha */}
            <div className="space-y-2">
              <Label htmlFor="confirmar-senha">Confirmar Nova Senha</Label>
              <div className="relative">
                <Input
                  id="confirmar-senha"
                  type={showConfirmar ? "text" : "password"}
                  value={confirmarSenha}
                  onChange={(e) => setConfirmarSenha(e.target.value)}
                  placeholder="Digite a senha novamente"
                  className="pr-10"
                  data-testid="confirmar-senha-input"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmar(!showConfirmar)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showConfirmar ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {novaSenha && confirmarSenha && novaSenha === confirmarSenha && (
                <div className="flex items-center gap-1 text-emerald-600 text-sm">
                  <CheckCircle2 className="w-4 h-4" />
                  Senhas coincidem
                </div>
              )}
            </div>

            {/* Erro */}
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}

            {/* Botões */}
            <div className="space-y-3 pt-2">
              <Button
                type="submit"
                className="w-full bg-teal-600 hover:bg-teal-700"
                disabled={loading}
                data-testid="trocar-senha-submit"
              >
                {loading ? (
                  "Alterando..."
                ) : (
                  <>
                    <KeyRound className="w-4 h-4 mr-2" />
                    Criar Nova Senha
                  </>
                )}
              </Button>
              
              <Button
                type="button"
                variant="ghost"
                className="w-full text-slate-500"
                onClick={handleLogout}
              >
                Sair e voltar ao login
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default TrocarSenha;
